/**
 * The desktop app sets up its own runner: signed in, with an agent signed in here, a prompt sent to
 * a project open on this computer should just run — not wait for somebody to find Agents › Connect
 * AI and switch three things on. So, while the app is signed in:
 *
 *  - the Vephus key: issued for the person and kept in the OS vault when there is none, and issued
 *    again when the server refuses the one there (an account deleted and made again, a key revoked);
 *  - the agent: the chosen one while it can run here, else the first that can;
 *  - the repositories: every linked one with a folder on this computer, except those unticked — the
 *    folders looked for by their git remote when the app opens (docs/refactor/53), so a command from
 *    the web for a project cloned here runs without anyone opening it first;
 *  - the runner: on, unless the person switched it off.
 *
 * Whatever the person changes by hand on that screen stands: switching it off sets `optedOut`,
 * unticking a repository puts it in `excluded`, and neither is undone here.
 */

import { autostartSet } from './desktop'
import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from './api'
import { isDesktop, vaultHas, vaultStore } from './desktop'
import { detectAgents, type DetectedAgent } from './localAgent'
import type { RepoRunnerStatus, RunnerSettings } from './localRunner'
import { findFoldersHere, folderFor, FOLDERS_CHANGED } from './repoFolders'
import { readRunnerSettings, saveRunnerSettings, useRunnerStatus } from './runnerHost'

/** What the settings should be, from what is on this computer; the same object when nothing changes. */
export function autoSettings(
  settings: RunnerSettings,
  agents: Pick<DetectedAgent, 'id' | 'installed' | 'runnable'>[],
  repositoriesWithFolder: string[],
): RunnerSettings {
  const runnable = agents.filter((a) => a.installed && a.runnable)
  if (runnable.length === 0) return settings
  const agent = runnable.some((a) => a.id === settings.agent) ? settings.agent : runnable[0]!.id
  const excluded = settings.excluded ?? []
  const added = repositoriesWithFolder.filter((name) => !settings.repositories.includes(name) && !excluded.includes(name))
  const repositories = added.length > 0 ? [...settings.repositories, ...added] : settings.repositories
  const enabled = settings.enabled || (!settings.optedOut && repositories.length > 0)
  if (agent === settings.agent && repositories === settings.repositories && enabled === settings.enabled) return settings
  return { ...settings, agent, repositories, enabled }
}

/** The server refused the key the runner holds. */
export function keyRefused(status: RepoRunnerStatus[]): boolean {
  return status.some((repo) => repo.state === 'key-problem')
}

// Whose key the vault holds. A key outlives the sign-in that made it: another account signing in on
// this computer must not have the runner keep taking the previous account's work.
const KEY_OWNER = 'devpulse.runnerKeyOwner'

function keyOwner(): string | null {
  try {
    return localStorage.getItem(KEY_OWNER)
  } catch {
    return null
  }
}

/** Issues a Vephus key for the person signed in and keeps it in the vault (also the screen's button). */
export async function issueKey(): Promise<void> {
  const me = await api.auth.me()
  // An agent's own sign-in has no person behind it, and no key of its own to issue.
  if (!me.userId) throw new Error('not-a-person')
  const issued = await api.me.issueCredential({ name: 'Vephus desktop', machineLabel: 'desktop runner' })
  await vaultStore(issued.key)
  try {
    localStorage.setItem(KEY_OWNER, me.userId)
  } catch {
    // Not remembered: the next start issues another key, which is harmless.
  }
}

// Once per app session: a refused key is replaced once, not in a loop if the new one fails too.
let reissued = false

/** Mounted once inside the signed-in app (App.tsx). Does nothing in a browser. */
export function useDesktopAutoRunner(): void {
  const desktop = isDesktop()
  const { data: agents } = useQuery({ queryKey: ['local-agents'], queryFn: detectAgents, staleTime: 60_000, enabled: desktop })
  const { data: repositories } = useQuery({ queryKey: ['repositories'], queryFn: api.repositories.list, enabled: desktop, refetchInterval: 60_000 })
  const status = useRunnerStatus()
  const refused = keyRefused(status)

  // The key first: a runner with no key only says "no key", and one of another account works for them.
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.auth.me, enabled: desktop })
  const userId = me?.userId
  useEffect(() => {
    if (!desktop || !userId) return
    void (async () => {
      try {
        if (!(await vaultHas()) || keyOwner() !== userId) {
          await issueKey()
          saveRunnerSettings(readRunnerSettings()) // restarts the loops with this key
        }
      } catch {
        // No vault (Linux without a keyring) or offline: the Connect AI screen says which.
      }
    })()
  }, [desktop, userId])

  useEffect(() => {
    if (!desktop || !refused || reissued) return
    reissued = true
    void issueKey()
      .then(() => saveRunnerSettings(readRunnerSettings())) // restarts the loops with the new key now
      .catch(() => {})
  }, [desktop, refused])

  // Each linked project's folder on this computer, found by its git remote (once per project per app session).
  useEffect(() => {
    if (!desktop || !repositories) return
    void findFoldersHere(repositories.filter((r) => r.linked))
  }, [desktop, repositories])

  useEffect(() => {
    if (!desktop || !agents || !repositories) return
    const sync = () => {
      const withFolder = repositories.filter((r) => r.linked && folderFor(r.name)).map((r) => r.name)
      const current = readRunnerSettings()
      const next = autoSettings(current, agents, withFolder)
      if (next !== current) {
        saveRunnerSettings(next)
        // Taking work switched itself on: the app also opens with the computer, unless the person chose (docs/refactor/28).
        if (next.enabled && !current.enabled) void openWithComputerOnce()
      }
    }
    sync()
    // A project opened on this computer is served from that moment, not on the next refetch.
    window.addEventListener(FOLDERS_CHANGED, sync)
    return () => window.removeEventListener(FOLDERS_CHANGED, sync)
  }, [desktop, agents, repositories])
}

/** The first time this computer starts taking work, the app opens with it from then on — once, never against a choice. */
export async function openWithComputerOnce(): Promise<void> {
  try {
    if (localStorage.getItem(AUTOSTART_CHOSEN) === '1') return
    await autostartSet(true)
    localStorage.setItem(AUTOSTART_CHOSEN, '1')
  } catch {
    // The system refused, or storage is private: the switch in Conectar agente still works.
  }
}

/** Set once the person (or this first switch-on) chose whether the app opens with the computer. */
export const AUTOSTART_CHOSEN = 'devpulse.autostartChosen'
